import { useState } from 'react';
import { 
  CreditCard, 
  DollarSign, 
  Users, 
  TrendingUp, 
  Search,
  Filter,
  Plus,
  Eye,
  Edit2,
  Check,
  X,
  AlertTriangle,
  Calendar,
  Clock,
  Receipt,
  Banknote,
  Wallet,
  MapPin,
  User,
  Phone,
  Mail
} from 'lucide-react';
import PageContainer from '../components/PageContainer';
import {
  Button,
  DataToolbar,
  Input,
  EmptyState,
  Modal,
  ResponsiveDataView,
  SectionPanel,
  StatCard,
  StatusBadge,
  DateInput,
  MonthInput,
  todayDateKey,
} from '../components/ui';

const Payments = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [paymentDate, setPaymentDate] = useState(() => todayDateKey());
  const [showForm, setShowForm] = useState(false);

  // Mock data for payments
  const paymentsData = [
    {
      id: 1,
      studentId: 1,
      studentName: 'Nguyễn Văn A',
      className: 'Toán 10A1',
      campus: 'Cơ sở Quận 1',
      phone: '0901234567',
      email: 'vana@email.com',
      amount: 1500000,
      dueDate: '2024-01-15',
      paidDate: '2024-01-10',
      status: 'paid',
      paymentMethod: 'bank_transfer',
      month: '2024-01',
      notes: 'Thanh toán đúng hạn'
    },
    {
      id: 2,
      studentId: 2,
      studentName: 'Trần Thị B',
      className: 'Lý 11B2',
      campus: 'Cơ sở Quận 7',
      phone: '0907654321',
      email: 'thib@email.com',
      amount: 1800000,
      dueDate: '2024-01-15',
      paidDate: null,
      status: 'overdue',
      paymentMethod: null,
      month: '2024-01',
      notes: 'Cần liên hệ'
    },
    {
      id: 3,
      studentId: 3,
      studentName: 'Lê Minh C',
      className: 'Anh 12C1',
      campus: 'Cơ sở Thủ Đức',
      phone: '0909876543',
      email: 'minhc@email.com',
      amount: 2200000,
      dueDate: '2024-01-20',
      paidDate: null,
      status: 'pending',
      paymentMethod: null,
      month: '2024-01',
      notes: ''
    },
    {
      id: 4,
      studentId: 4,
      studentName: 'Phạm Thu D',
      className: 'Toán 10A1',
      campus: 'Cơ sở Quận 1',
      phone: '0912345678',
      email: 'thud@email.com',
      amount: 1500000,
      dueDate: '2024-01-15',
      paidDate: '2024-01-12',
      status: 'paid',
      paymentMethod: 'cash',
      month: '2024-01',
      notes: 'Thanh toán bằng tiền mặt'
    },
    {
      id: 5,
      studentId: 5,
      studentName: 'Hoàng Văn E',
      className: 'Hóa 11A3',
      campus: 'Cơ sở Quận 1',
      phone: '0923456789',
      email: 'vane@email.com',
      amount: 1700000,
      dueDate: '2024-01-25',
      paidDate: null,
      status: 'pending',
      paymentMethod: null,
      month: '2024-01',
      notes: ''
    },
    {
      id: 6,
      studentId: 6,
      studentName: 'Vũ Thị F',
      className: 'Lý 11B2',
      campus: 'Cơ sở Quận 7',
      phone: '0934567890',
      email: 'thif@email.com',
      amount: 1800000,
      dueDate: '2024-01-10',
      paidDate: null,
      status: 'overdue',
      paymentMethod: null,
      month: '2024-01',
      notes: 'Quá hạn 5 ngày'
    }
  ];

  const classes = [
    'Toán 10A1', 'Lý 11B2', 'Anh 12C1', 'Hóa 11A3'
  ];

  const filteredPayments = paymentsData.filter(payment => {
    const matchesSearch = payment.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.className.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = selectedStatus === 'all' || payment.status === selectedStatus;
    const matchesClass = selectedClass === 'all' || payment.className === selectedClass;
    const matchesMonth = payment.month === selectedMonth;
    
    return matchesSearch && matchesStatus && matchesClass && matchesMonth;
  });

  const stats = {
    totalRevenue: paymentsData.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0),
    pendingAmount: paymentsData.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0),
    overdueAmount: paymentsData.filter(p => p.status === 'overdue').reduce((sum, p) => sum + p.amount, 0),
    totalStudents: paymentsData.length,
    paidStudents: paymentsData.filter(p => p.status === 'paid').length,
    pendingStudents: paymentsData.filter(p => p.status === 'pending').length,
    overdueStudents: paymentsData.filter(p => p.status === 'overdue').length
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-success-100 text-success-800';
      case 'pending': return 'bg-warning-100 text-warning-800';
      case 'overdue': return 'bg-danger-100 text-danger-800';
      default: return 'bg-ink-100 text-ink-800';
    }
  };

  const getStatusTone = (status: string): 'success' | 'warning' | 'danger' | 'neutral' | 'info' => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'overdue': return 'danger';
      default: return 'neutral';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return <Check className="w-4 h-4 text-success-600" />;
      case 'pending': return <Clock className="w-4 h-4 text-warning-600" />;
      case 'overdue': return <AlertTriangle className="w-4 h-4 text-danger-600" />;
      default: return <X className="w-4 h-4 text-ink-600" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'paid': return 'Đã thanh toán';
      case 'pending': return 'Chờ thanh toán';
      case 'overdue': return 'Quá hạn';
      default: return status;
    }
  };

  const getPaymentMethodText = (method: string | null) => {
    switch (method) {
      case 'bank_transfer': return 'Chuyển khoản';
      case 'cash': return 'Tiền mặt';
      case 'card': return 'Thẻ';
      default: return 'Chưa xác định';
    }
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('vi-VN') + '₫';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Chưa thanh toán';
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  return (
    <PageContainer
      title="Học phí"
      subtitle="Theo dõi thanh toán học phí của học sinh."
      actions={(
        <>
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => setShowForm(true)}>
            Thêm thanh toán
          </Button>
        </>
      )}
    >

      {/* Stats Cards */}
      <div className="grid gap-px overflow-hidden rounded-lg border border-ink-200 bg-ink-200 grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Doanh thu tháng"
          value={formatCurrency(stats.totalRevenue)}
          detail="+12%"
          icon={<DollarSign className="h-5 w-5" />}
          tone="action"
        />
        <StatCard
          label="Chờ thu"
          value={formatCurrency(stats.pendingAmount)}
          detail={`${stats.pendingStudents} học sinh`}
          icon={<Wallet className="h-5 w-5" />}
          tone="warning"
        />
        <StatCard
          label="Quá hạn"
          value={formatCurrency(stats.overdueAmount)}
          detail={`${stats.overdueStudents} học sinh`}
          icon={<AlertTriangle className="h-5 w-5" />}
          tone="danger"
        />
        <StatCard
          label="Đã thanh toán"
          value={`${stats.paidStudents}/${stats.totalStudents}`}
          detail={`${((stats.paidStudents / stats.totalStudents) * 100).toFixed(1)}%`}
          icon={<Users className="h-5 w-5" />}
          tone="academy"
        />
      </div>

      {/* Filters */}
      <DataToolbar
          className="sm:items-center"
          controlsLayoutClassName="grid flex-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center"
      >
          <MonthInput aria-label="Tháng" className="lg:w-36" value={selectedMonth} onChange={setSelectedMonth} clearable={false} />
          <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Tìm học sinh, lớp…"
              leftIcon={<Search className="h-4 w-4" />}
              aria-label="Tìm kiếm thanh toán"
              className="min-h-9 lg:w-72"
          />
            <select
                aria-label="Lọc theo lớp học"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="min-h-9 w-full rounded-lg border border-ink-300 bg-white px-3 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100 lg:w-44"
              >
                <option value="all">Tất cả lớp</option>
                {classes.map(className => (
                  <option key={className} value={className}>{className}</option>
                ))}
              </select>
            <select
                aria-label="Lọc theo trạng thái"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="min-h-9 w-full rounded-lg border border-ink-300 bg-white px-3 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100 lg:w-44"
              >
                <option value="all">Mọi trạng thái</option>
                <option value="paid">Đã thanh toán</option>
                <option value="pending">Chờ thanh toán</option>
                <option value="overdue">Quá hạn</option>
              </select>
      </DataToolbar>

      {/* Payments Table */}
      <SectionPanel className="overflow-hidden max-lg:rounded-none max-lg:border-0 max-lg:bg-transparent max-lg:shadow-none">
        {filteredPayments.length === 0 ? (
          <EmptyState
            title="Không tìm thấy thanh toán"
            description="Thử thay đổi bộ lọc hoặc ghi nhận thanh toán mới."
          />
        ) : (
          <ResponsiveDataView
            desktopLabel="Bảng thanh toán học phí"
            mobileLabel="Thẻ thanh toán học phí"
            desktop={(
          <table className="min-w-full divide-y divide-ink-200 text-sm">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">
                  Học sinh
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">
                  Lớp học
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">
                  Số tiền
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">
                  Hạn thanh toán
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">
                  Trạng thái
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">
                  Phương thức
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-ink-500">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200 bg-white">
              {filteredPayments.map((payment) => (
                <tr key={payment.id} className="transition-colors duration-200 hover:bg-ink-100/60">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-full bg-ink-100">
                        <span className="text-sm font-semibold text-ink-700">
                          {payment.studentName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-ink-900">{payment.studentName}</div>
                        <div className="flex items-center text-sm text-ink-500">
                          <Phone className="w-3 h-3 mr-1" />
                          {payment.phone}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-ink-900">{payment.className}</div>
                    <div className="flex items-center text-sm text-ink-500">
                      <MapPin className="w-3 h-3 mr-1" />
                      {payment.campus}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-semibold text-ink-900">{formatCurrency(payment.amount)}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-ink-900">{formatDate(payment.dueDate)}</div>
                    {payment.paidDate && (
                      <div className="text-sm text-action-700">Đã thanh toán: {formatDate(payment.paidDate)}</div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge tone={getStatusTone(payment.status)} icon={getStatusIcon(payment.status)}>
                      {getStatusText(payment.status)}
                    </StatusBadge>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-ink-900">{getPaymentMethodText(payment.paymentMethod)}</div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <Button variant="ghost" size="icon" icon={<Eye className="h-4 w-4" />} aria-label={`Xem chi tiết thanh toán của ${payment.studentName}`} />
                      <Button variant="ghost" size="icon" icon={<Edit2 className="h-4 w-4" />} aria-label={`Sửa thanh toán của ${payment.studentName}`} />
                      <Button variant="ghost" size="icon" icon={<Receipt className="h-4 w-4" />} aria-label={`Xem biên lai của ${payment.studentName}`} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
            )}
            mobile={(
              <div className="grid gap-3">
                {filteredPayments.map((payment) => (
                  <article key={payment.id} className="rounded-lg border border-ink-200 bg-white p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="truncate text-base font-semibold text-ink-900">{payment.studentName}</h3>
                        <p className="mt-1 truncate text-sm text-ink-500">{payment.className} - {payment.campus}</p>
                      </div>
                      <StatusBadge tone={getStatusTone(payment.status)} icon={getStatusIcon(payment.status)} className="shrink-0">
                        {getStatusText(payment.status)}
                      </StatusBadge>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-ink-500">Số tiền</p>
                        <p className="font-semibold text-ink-900">{formatCurrency(payment.amount)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-ink-500">Hạn thanh toán</p>
                        <p className="font-medium text-ink-800">{formatDate(payment.dueDate)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-ink-500">Phương thức</p>
                        <p className="font-medium text-ink-800">{getPaymentMethodText(payment.paymentMethod)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-ink-500">Liên hệ</p>
                        <p className="font-medium text-ink-800">{payment.phone}</p>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end gap-2 border-t border-ink-200 pt-3">
                      <Button variant="secondary" size="sm" icon={<Eye className="h-4 w-4" />} aria-label={`Xem chi tiết thanh toán của ${payment.studentName}`}>Xem chi tiết</Button>
                      <Button variant="ghost" size="icon" icon={<Edit2 className="h-4 w-4" />} aria-label={`Sửa thanh toán của ${payment.studentName}`} />
                      <Button variant="ghost" size="icon" icon={<Receipt className="h-4 w-4" />} aria-label={`Xem biên lai của ${payment.studentName}`} />
                    </div>
                  </article>
                ))}
              </div>
            )}
          />
        )}
      </SectionPanel>

      <Modal
        open={showForm}
        title="Thêm thanh toán"
        onClose={() => setShowForm(false)}
        className="sm:max-w-2xl"
        footer={(
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="secondary" onClick={() => setShowForm(false)}>
              Hủy
            </Button>
            <Button>
              Thêm thanh toán
            </Button>
          </div>
        )}
      >
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">Học sinh</label>
                    <select className="min-h-10 w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100">
                      <option value="">Chọn học sinh</option>
                      {paymentsData.map(payment => (
                        <option key={payment.studentId} value={payment.studentId}>
                          {payment.studentName} - {payment.className}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">Số tiền (VNĐ)</label>
                    <input
                      type="number"
                      placeholder="1500000"
                      className="min-h-10 w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DateInput label="Ngày thanh toán" value={paymentDate} onChange={setPaymentDate} clearable={false} />
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">Phương thức thanh toán</label>
                    <select className="min-h-10 w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100">
                      <option value="">Chọn phương thức</option>
                      <option value="cash">Tiền mặt</option>
                      <option value="bank_transfer">Chuyển khoản</option>
                      <option value="card">Thẻ</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">Ghi chú</label>
                  <textarea
                    placeholder="Ghi chú về thanh toán..."
                    rows={3}
                    className="min-h-10 w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100"
                  />
                </div>
              </div>
      </Modal>
    </PageContainer>
  );
};

export default Payments;
